const BACKEND_URL =
  import.meta.env.VITE_BACKEND_URL ||
  (import.meta.env.DEV ? "http://localhost:4001" : "https://backend-dev-api.backend.perpx.ai");

const BASE = `${BACKEND_URL.replace(/\/$/, "")}/api/hyperliquid`;

export interface HlOrderRequest {
  symbol: string;
  side: "BUY" | "SELL";
  type: "MARKET" | "LIMIT";
  quantity: string;
  price?: string;
  leverage?: number;
  reduceOnly?: boolean;
  timeInForce?: "GTC" | "IOC" | "POST_ONLY";
  userAddress: string;
}

export interface HlRoutingInfo {
  recommended: "gmx" | "hyperliquid";
  price: number;
  reason: string;
  savings: number;
  savingsPercent: number;
  alternatives: {
    gmx: { price: number; available: boolean; effectivePrice: number };
    hyperliquid: { price: number; available: boolean; effectivePrice: number };
  };
}

function authHeaders(token?: string) {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

// ── Agent setup ───────────────────────────────────────────────────────────────

export async function checkHlApiKey(userAddress: string): Promise<boolean> {
  const res = await fetch(`${BASE}/check-apikey/${userAddress}`);
  const json = await res.json();
  return json?.data?.hasValidApiKey ?? false;
}

export async function setupHlAgent(token: string): Promise<{
  agentWalletAddress: string;
  custodyWalletAddress: string;
  isNew: boolean;
}> {
  const res = await fetch(`${BASE}/setup`, {
    method: "POST",
    headers: authHeaders(token),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? "Agent setup failed");
  return json.data;
}

// ── Browser-wallet EIP-712 flow (optional) ───────────────────────────────────

export async function prepareHlApproval(walletAddress: string): Promise<{
  typedData: any;
  agentWalletAddress: string;
  nonce: number;
}> {
  const res = await fetch(`${BASE}/prepare-approval`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ walletAddress }),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? "Prepare approval failed");
  return json.data;
}

export async function completeHlApproval(
  walletAddress: string,
  signature: string,
  custodyWalletAddress?: string,
): Promise<{ agentWalletAddress: string }> {
  const res = await fetch(`${BASE}/complete-approval`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ walletAddress, signature, custodyWalletAddress }),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? "Complete approval failed");
  return json.data;
}

// ── Trading ───────────────────────────────────────────────────────────────────

export async function placeHlOrder(req: HlOrderRequest, token: string) {
  const res = await fetch(`${BASE}/order`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(req),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? "Order placement failed");
  return json.data;
}

export async function closeHlPosition(
  symbol: string,
  size: string,
  side: "LONG" | "SHORT",
  token: string,
): Promise<{ status: string; filledQty?: string; avgPrice?: string }> {
  const res = await fetch(`${BASE}/close-position`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ symbol, size, side }),
  });
  const json = await res.json();
  if (!json.success) {
    const msg = json.detail ? `${json.error} — ${json.detail}` : json.error ?? "Close position failed";
    throw new Error(msg);
  }
  return json.data;
}

export async function cancelHlOrder(
  orderId: string,
  symbol: string,
  token: string,
) {
  const res = await fetch(`${BASE}/order/${orderId}?symbol=${symbol}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? "Cancel failed");
  return json.data;
}

// ── Positions & balances ──────────────────────────────────────────────────────

export async function getHlPositions(userAddress: string, token?: string) {
  const res = await fetch(`${BASE}/positions/${userAddress}`, {
    headers: authHeaders(token),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? "Failed to fetch positions");
  return json.data.positions ?? [];
}

export async function getHlBalances(userAddress: string, token?: string) {
  const res = await fetch(`${BASE}/balances/${userAddress}`, {
    headers: authHeaders(token),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? "Failed to fetch balances");
  return json.data.balances ?? [];
}

// ── Market data ───────────────────────────────────────────────────────────────

export async function getHlTicker(symbol: string) {
  const res = await fetch(`${BASE}/ticker/${symbol}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? "Failed to fetch ticker");
  return json.data;
}

export async function getHlMarkets(): Promise<string[]> {
  const res = await fetch(`${BASE}/markets`);
  const json = await res.json();
  return json?.data?.markets ?? [];
}

// ── Smart routing ─────────────────────────────────────────────────────────────

export async function getRoutingRecommendation(
  symbol: string,
  side: "BUY" | "SELL",
  signal?: AbortSignal,
): Promise<HlRoutingInfo | null> {
  try {
    const res = await fetch(`${BASE}/routing/${symbol}?side=${side}`, { signal });
    const json = await res.json();
    return json?.data?.routing ?? null;
  } catch {
    return null;
  }
}

// ── Deposit ───────────────────────────────────────────────────────────────────

export async function getCustodyUSDCBalance(_userAddress: string, token?: string): Promise<string> {
  const res = await fetch(`${BASE}/usdc-balance`, {
    headers: authHeaders(token),
  });
  const json = await res.json();
  if (!json.success) return "0";
  return json.data?.usdc ?? "0";
}

export async function depositToHyperliquid(
  amount: string,
  token: string,
): Promise<{ txHash: string; amountUSDC: string; message: string }> {
  const res = await fetch(`${BASE}/deposit`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ amount }),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? "Deposit failed");
  return json.data;
}

export interface HlTrade {
  symbol: string;
  side: "BUY" | "SELL";
  price: string;
  size: string;
  fee: string;
  feeToken: string;
  closedPnl: string;
  time: number;
  hash: string;
  orderId?: string;
  dir: string;
  platform: string;
}

export async function getHlTradeHistory(
  userAddress: string,
  startTime?: number,
  token?: string,
): Promise<HlTrade[]> {
  const params = startTime ? `?startTime=${startTime}` : "";
  const res = await fetch(`${BASE}/trades/${userAddress}${params}`, {
    headers: authHeaders(token),
  });
  const json = await res.json();
  if (!json.success) return [];
  return json.data?.trades ?? [];
}

export async function withdrawFromHl(
  amount: string,
  token: string,
  destination?: string,
): Promise<{ message: string }> {
  const res = await fetch(`${BASE}/withdraw`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ amount, destination }),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? "HL withdrawal failed");
  return json.data;
}

export async function getSupportedVenues(): Promise<
  Array<{ id: string; name: string; enabled: boolean }>
> {
  const res = await fetch(`${BASE}/venues`);
  const json = await res.json();
  return json?.data?.venues ?? [];
}
